/**
 * THE FILE — rows, figures and the state ledger.
 *
 * Everything here is set the way a workpaper is set: a label on the left, its
 * source beside it, its figure hard right on a tabular grid so the digits of
 * one line sit above the digits of the next. Rows are separated by hairlines,
 * never by boxes, and a total is marked with an ink rule above and below —
 * the printed convention, because the people reading this have read ten
 * thousand pages set that way and know instantly what it means.
 *
 * NOTHING HERE COMPUTES. Every figure arrives already formatted by the server,
 * every state arrives already decided. A component that re-derived a total
 * would be a second answer to a question the professional record has already
 * answered once, and two answers is the failure this product exists to remove.
 */
import { clsx } from "clsx";
import type { ComponentPropsWithoutRef, ReactNode } from "react";
import type { Tone } from "@leveri/contracts";
import { StateText } from "./marks.js";
import { Citation } from "./text.js";
import { markerTone } from "./tone.js";

export interface FigureProps {
  label: ReactNode;
  /** Where the figure was read from, or which way a difference runs. */
  citation?: ReactNode;
  /** The server's own rendering of the amount. Never a number to format here. */
  value: ReactNode;
  /** A total: ink rule above AND below, and the label in medium. */
  total?: boolean | undefined;
  /** The first row in a run carries no top hairline — the section already opened. */
  first?: boolean | undefined;
  className?: string | undefined;
}

/** A ledger row: label and citation left, a tabular figure right. */
export function Figure({ label, citation, value, total, first, className }: FigureProps) {
  return (
    <div
      className={clsx(
        "grid grid-cols-[minmax(0,1fr)_200px] items-baseline gap-4 py-3 md:grid-cols-[minmax(0,1fr)_240px]",
        total ? "border-y-[1.5px] border-rule-strong" : !first && "border-t border-rule-soft",
        className
      )}
    >
      <div className={clsx("min-w-0", total ? "font-medium text-ink" : "text-ink")}>
        {label}
        {citation ? <Citation className="ml-2">{citation}</Citation> : null}
      </div>
      <div className={clsx("text-right text-[22px] leading-tight text-ink [font-variant-numeric:tabular-nums]", total && "font-medium")}>
        {value}
      </div>
    </div>
  );
}

export interface DefinitionItem {
  /**
   * A stable identity for the row. Two definition rows can legitimately share a
   * term (two records, both "Source"), so the term alone is not a key.
   */
  key?: string | undefined;
  term: ReactNode;
  value: ReactNode;
  citation?: ReactNode;
}

export interface DefinitionsProps {
  items: readonly DefinitionItem[];
  /** A narrower term column, for a definition list inside a dock or a card. */
  compact?: boolean | undefined;
  className?: string | undefined;
}

/**
 * Definition rows — a stone term, its value, and where the value came from.
 *
 * The term column is a fixed width so the values form a column of their own;
 * a reader scanning for one fact scans one edge rather than a ragged left
 * margin. `contents` on the wrapper keeps each pair a grid participant, so the
 * dt and dd of a row still line up when the value wraps to three lines.
 */
export function Definitions({ items, compact, className }: DefinitionsProps) {
  return (
    <dl
      className={clsx(
        "grid gap-x-6 gap-y-2.5 text-[15px]",
        compact ? "grid-cols-[130px_minmax(0,1fr)]" : "grid-cols-[150px_minmax(0,1fr)] md:grid-cols-[170px_minmax(0,1fr)]",
        className
      )}
    >
      {items.map((item, index) => (
        <div key={item.key ?? index} className="contents">
          <dt className="text-stone">{item.term}</dt>
          <dd className="min-w-0">
            <div className="text-ink">{item.value}</div>
            {item.citation ? <div className="mt-0.5">{item.citation}</div> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface RuledListProps {
  children: ReactNode;
  className?: string | undefined;
}

/** Rows separated by hairlines. The first row carries none — the list opens on it. */
export function RuledList({ children, className }: RuledListProps) {
  return <div className={clsx("divide-y divide-rule-soft", className)}>{children}</div>;
}

const ROW = "block w-full py-3 text-left";
/**
 * When the row is the target, the WHOLE row is the target — not a link buried
 * in the first cell. A 44px-tall row is an easy thing to hit and an obvious
 * thing to hit; a 13px filename inside it is neither.
 */
const ROW_TARGET = "transition-colors duration-150 hover:bg-band focus:bg-band focus:outline-none";

type RuledRowBase = { className?: string | undefined; children?: ReactNode };
type RuledRowProps =
  | (RuledRowBase & { as?: "div" | undefined } & Omit<ComponentPropsWithoutRef<"div">, "className" | "children">)
  | (RuledRowBase & { as: "li" } & Omit<ComponentPropsWithoutRef<"li">, "className" | "children">)
  | (RuledRowBase & { as: "button" } & Omit<ComponentPropsWithoutRef<"button">, "className" | "children">)
  | (RuledRowBase & { as: "a" } & Omit<ComponentPropsWithoutRef<"a">, "className" | "children">);

/**
 * One row of a ruled list. `as="button"` / `as="a"` make the row itself the
 * control; anything else is a plain row. The remaining props pass straight
 * through, so a caller can carry aria-* and data-* without a wrapper.
 */
export function RuledRow(props: RuledRowProps) {
  if (props.as === "button") {
    const { as: _as, className, children, type, ...rest } = props;
    // type="button" by default: a row inside a form that submits it on click is
    // a bug that only shows up once the row is used inside a form.
    return (
      <button type={type ?? "button"} className={clsx(ROW, ROW_TARGET, className)} {...rest}>
        {children}
      </button>
    );
  }
  if (props.as === "a") {
    const { as: _as, className, children, ...rest } = props;
    return (
      <a className={clsx(ROW, ROW_TARGET, className)} {...rest}>
        {children}
      </a>
    );
  }
  if (props.as === "li") {
    const { as: _as, className, children, ...rest } = props;
    return (
      <li className={clsx(ROW, className)} {...rest}>
        {children}
      </li>
    );
  }
  const { as: _as, className, children, ...rest } = props;
  return (
    <div className={clsx(ROW, className)} {...rest}>
      {children}
    </div>
  );
}

/**
 * THE STATE LEDGER — the professional position, broken out.
 *
 * ONE MARKER CANNOT CARRY THIS POSITION. Work whose difference is fully
 * explained but whose accounting treatment is still unsupported is genuinely
 * both resolved and unfinished, and a single badge would have to lie in one
 * direction or the other. So the position is a ledger: the overall answer, then
 * the parts it is made of, each with its own words and its own marker.
 *
 * The server sends the rows already decided and already ordered, with the
 * overall position first. That row is set in medium weight — serial position
 * says the answer everything else qualifies belongs at the top, and weight is
 * how a reader is told which line is the answer. Nothing else is inferred here:
 * this component cannot make the overall row read as established while a part
 * is open, because it never computes the overall row at all.
 */
export interface StateLedgerRowView {
  key: string;
  term: string;
  value: string;
  tone: Tone;
}

export interface StateLedgerProps {
  rows: readonly StateLedgerRowView[];
  className?: string | undefined;
}

export function StateLedger({ rows, className }: StateLedgerProps) {
  if (rows.length === 0) return null;
  return (
    <dl className={clsx("flex flex-col gap-2.5 text-[13px]", className)}>
      {rows.map((row, index) => (
        <div key={row.key}>
          <dt className="text-stone">{row.term}</dt>
          <dd className={clsx("mt-0.5 text-ink", index === 0 && "font-medium")}>
            <StateText tone={markerTone(row.tone)}>{row.value}</StateText>
          </dd>
        </div>
      ))}
    </dl>
  );
}
